import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, Tag, Button, Empty, Modal, Input, Space, Tooltip } from 'antd';
import { BellOutlined, CheckOutlined, EyeOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import bilateralesService from '../../services/bilateralesService';
import { formatDate } from '../../variables/constants';
import TableExportButton from '../../components/TableExportButton';

/**
 * Onglet "Bilatérales" séparé des relances CIMA conventionnelles -
 * promesses séance tenante (J-2), pas de pénalité.
 */
export default function RelancesBilateralesTab() {
  const { t } = useTranslation('relances');
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resoudreId, setResoudreId] = useState(null);
  const [motif, setMotif] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await bilateralesService.listRelances();
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resoudre = async () => {
    if (!motif.trim()) {
      toast.error(t('bilaterales.motifObligatoire'));
      return;
    }
    await bilateralesService.resoudreRelance(resoudreId, motif);
    toast.success(t('bilaterales.relanceResolue'));
    setResoudreId(null);
    setMotif('');
    load();
  };

  const niveauJours = (j) => {
    if (j == null) return null;
    if (j < 0) return { color: 'red', label: t('bilaterales.enRetard', { n: Math.abs(j) }) };
    if (j <= 2) return { color: 'volcano', label: t('bilaterales.jMoins', { n: j }) };
    if (j <= 5) return { color: 'gold', label: t('bilaterales.jMoins', { n: j }) };
    return { color: 'default', label: t('bilaterales.jMoins', { n: j }) };
  };

  const columns = [
    {
      title: t('bilaterales.colonnes.rencontre'),
      dataIndex: 'rbReference',
    },
    { title: t('bilaterales.colonnes.compagnie'), dataIndex: 'compagnieRaisonSociale' },
    {
      title: t('bilaterales.colonnes.sinistre'),
      dataIndex: 'numeroSinistreLocal',
      render: (v, r) => (
        <Button type="link" onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)} style={{ paddingLeft: 0 }}>
          {v}
        </Button>
      ),
      export: (v) => v ?? '',
    },
    {
      title: t('bilaterales.colonnes.datePromise'),
      dataIndex: 'datePromesse',
      render: (v) => <Tag color="gold">{formatDate(v)}</Tag>,
      format: 'date',
    },
    {
      title: t('bilaterales.colonnes.rappel'),
      dataIndex: 'dateRappel',
      render: formatDate,
      format: 'date',
    },
    {
      title: t('bilaterales.colonnes.echeance'),
      dataIndex: 'joursAvantPromesse',
      render: (v) => {
        const meta = niveauJours(v);
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : '-';
      },
      export: (v) => (niveauJours(v)?.label ?? '-'),
    },
    {
      title: t('bilaterales.colonnes.statut'),
      dataIndex: 'statut',
      render: (v) => <Tag>{v}</Tag>,
      export: (v) => v ?? '',
    },
    {
      title: t('bilaterales.colonnes.envois'),
      dataIndex: 'nombreEnvois',
      align: 'center',
      numeric: true,
    },
    {
      title: '',
      exportable: false,
      render: (_, r) => (
        <Space>
          <Tooltip title={t('bilaterales.ouvrirDossier')}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => {
              // Navigation vers la RB. Le drawer est ouvert manuellement depuis la session.
              // On laisse la nav simple ici (lien sinistre déjà cliquable).
            }} />
          </Tooltip>
          {r.statut !== 'RESOLUE' && (
            <Button size="small" icon={<CheckOutlined />} onClick={() => setResoudreId(r.rlbTrackingId)}>
              {t('bilaterales.resoudre')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  if (!loading && items.length === 0) {
    return (
      <div style={{ padding: 40 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('bilaterales.vide')}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{ marginBottom: 12, color: '#6b7280' }}>
        <BellOutlined /> {t('bilaterales.intro')}
      </div>
      <TableExportButton
        floating
        data={items}
        columns={columns}
        filename="relances-bilaterales"
        title={t('bilaterales.exportTitre')}
        subtitle={t('bilaterales.exportSousTitre', { n: items.length })}
        count={items.length}
      />
      <Table
        rowKey="rlbTrackingId"
        loading={loading}
        dataSource={items}
        columns={columns}
        size="middle"
        pagination={{ pageSize: 30 }}
      />

      <Modal
        open={!!resoudreId}
        title={t('bilaterales.modale.titre')}
        onCancel={() => { setResoudreId(null); setMotif(''); }}
        onOk={resoudre}
        okText={t('bilaterales.resoudre')}
      >
        <p style={{ color: '#6b7280', marginTop: 0 }}>
          {t('bilaterales.modale.aide')}
        </p>
        <Input.TextArea
          rows={3}
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          placeholder={t('bilaterales.modale.motif')}
        />
      </Modal>
    </div>
  );
}
